import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { scheduleApi } from '../lib/api';
import { readGuestEvents, writeGuestEvents } from './useScheduleStore';

const POLL_MS = 60 * 1000; // 1 minute
const ONE_HOUR = 60 * 60 * 1000;
const TWENTY_FOUR_HOURS = 24 * ONE_HOUR;

/**
 * Polls upcoming appointments (signed-in: backend, guest: localStorage) and
 * fires browser notification + toast at the 24h and 1h marks (DIL's policy).
 *
 * Mounted once at the top of <App />.
 */
export default function useScheduleReminders() {
  const { user, loading } = useAuth();
  const firedRef = useRef(new Set()); // `${id}:${mark}` already notified

  useEffect(() => {
    if (loading) return undefined;
    const isGuest = !user || user === false;

    let timer = null;

    const showAlert = (ev, mark, label) => {
      const key = `${ev.id}:${mark}`;
      if (firedRef.current.has(key)) return false;
      firedRef.current.add(key);
      const who = ev.customer_name ? ` with ${ev.customer_name}` : '';
      const body = `${label} · ${ev.title || 'Appointment'}${who}`;
      try {
        if (typeof window !== 'undefined' && 'Notification' in window
            && Notification.permission === 'granted') {
          const n = new Notification(ev.title || 'Upcoming appointment', { body, tag: key });
          n.onclick = () => { window.focus(); window.location.href = '/schedule'; };
        }
      } catch (_) { /* ignore */ }
      toast(ev.title || 'Upcoming appointment', {
        description: body,
        action: { label: 'Open', onClick: () => { window.location.href = '/schedule'; } },
      });
      return true;
    };

    const tickAuth = async () => {
      try {
        const { data } = await scheduleApi.list('upcoming');
        const now = Date.now();
        for (const ev of data || []) {
          if (!ev || ev.status !== 'scheduled') continue;
          const startTs = new Date(ev.start_at).getTime();
          if (isNaN(startTs)) continue;
          const m24 = startTs - TWENTY_FOUR_HOURS;
          if (!ev.reminded_24h && now >= m24 && now < startTs - ONE_HOUR) {
            if (showAlert(ev, '24h', '24 hours before')) {
              try { await scheduleApi.update(ev.id, { reminded_24h: true }); } catch (_) {}
            }
          }
          const m1 = startTs - ONE_HOUR;
          if (!ev.reminded_1h && now >= m1 && now < startTs + ONE_HOUR) {
            if (showAlert(ev, '1h', '1 hour before')) {
              try { await scheduleApi.update(ev.id, { reminded_1h: true }); } catch (_) {}
            }
          }
        }
      } catch (_) { /* silent polling */ }
    };

    const tickGuest = () => {
      const all = readGuestEvents();
      if (!all.length) return;
      const now = Date.now();
      let changed = false;
      const next = all.map((ev) => {
        if (!ev || ev.status !== 'scheduled') return ev;
        const startTs = new Date(ev.start_at).getTime();
        if (isNaN(startTs)) return ev;
        let updated = ev;
        const m24 = startTs - TWENTY_FOUR_HOURS;
        if (!ev.reminded_24h && now >= m24 && now < startTs - ONE_HOUR) {
          if (showAlert(ev, '24h', '24 hours before')) {
            updated = { ...updated, reminded_24h: true };
            changed = true;
          }
        }
        const m1 = startTs - ONE_HOUR;
        if (!updated.reminded_1h && now >= m1 && now < startTs + ONE_HOUR) {
          if (showAlert(ev, '1h', '1 hour before')) {
            updated = { ...updated, reminded_1h: true };
            changed = true;
          }
        }
        return updated;
      });
      if (changed) writeGuestEvents(next);
    };

    const tick = isGuest ? tickGuest : tickAuth;
    tick();
    timer = setInterval(tick, POLL_MS);
    return () => { if (timer) clearInterval(timer); };
  }, [user, loading]);
}
